import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const getGroupsViewCardStyles = (theme: Theme) =>
  StyleSheet.create({
    groupCard: {
      position: 'relative',
      backgroundColor: theme.background.card,
      borderRadius: 20,
      marginBottom: 14,
      paddingHorizontal: 20,
      paddingVertical: 20,
      borderWidth: 1,
      borderColor: theme.gray[200],
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        boxShadow: theme.shadows.cardElevated,
      }),
      ...(Platform.OS !== 'web' && {
        shadowColor: theme.shadow,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.12,
        shadowRadius: 10,
        elevation: 4,
      }),
    },
    groupCardRecessed: {
      opacity: 0.88,
      backgroundColor: theme.background.inset,
      borderColor: theme.gray[300],
    },
    groupCardGlow: {
      position: 'absolute',
      top: -48,
      right: -48,
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: theme.primary + '14',
    },
    groupCardPressed: { opacity: 0.9 },
    groupCardHover: { opacity: 0.98 },
    groupCardTopRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 18,
      zIndex: 1,
    },
    groupCardTitleBlock: {
      flex: 1,
      minWidth: 0,
      paddingRight: 10,
      gap: 4,
    },
    categoryLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.primary,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
    },
    categoryLabelMuted: {
      color: theme.gray[500],
    },
    groupName: {
      fontSize: 20,
      fontWeight: '800',
      color: theme.black,
      letterSpacing: -0.3,
    },
    groupNameMuted: {
      color: theme.gray[600],
    },
    rankBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: theme.borderRadius.md,
      flexShrink: 0,
    },
    rankBadgeGold: {
      backgroundColor: theme.primary,
    },
    rankBadgeNeutral: {
      backgroundColor: theme.background.cardElevated,
      borderWidth: 1,
      borderColor: theme.gray[300],
    },
    rankBadgeTextGold: {
      fontSize: 14,
      fontWeight: '900',
      color: theme.onPrimary,
      letterSpacing: -0.2,
    },
    rankBadgeTextNeutral: {
      fontSize: 14,
      fontWeight: '900',
      color: theme.black,
      letterSpacing: -0.2,
    },
    rankBadgeTextMuted: {
      color: theme.gray[500],
    },
    companyTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      flex: 1,
      minWidth: 0,
    },
    companyLogoLarge: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: theme.white,
      borderWidth: 1,
      borderColor: theme.gray[200],
      overflow: 'hidden',
      flexShrink: 0,
    },
    groupNameWithLogo: {
      flex: 1,
      minWidth: 0,
    },
    groupCardBottomRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      zIndex: 1,
    },
    // ── Member stack (used by GroupsViewMemberStack sub-component) ────────────
    memberStack: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    memberDot: {
      width: 32,
      height: 32,
      borderRadius: 16,
      borderWidth: 2,
      borderColor: theme.background.card,
      marginRight: -10,
    },
    memberDotOnRecessed: {
      borderColor: theme.background.inset,
    },
    memberDotLast: {
      marginRight: 0,
    },
    memberDotTintA: {
      backgroundColor: theme.primary + '44',
    },
    memberDotTintB: {
      backgroundColor: theme.secondary + '44',
    },
    memberDotEmpty: {
      backgroundColor: theme.background.cardElevated,
      borderStyle: 'dashed',
    },
    memberOverflowChip: {
      width: 32,
      height: 32,
      borderRadius: 16,
      borderWidth: 2,
      borderColor: theme.background.card,
      backgroundColor: theme.background.cardElevated,
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: -10,
    },
    memberOverflowOnRecessed: {
      borderColor: theme.background.inset,
    },
    memberOverflowText: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.gray[600],
    },
    // ── Prize block ───────────────────────────────────────────────────────────
    prizeBlock: {
      alignItems: 'flex-end',
    },
    prizeLabel: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.gray[500],
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 4,
    },
    prizeLabelMuted: {
      color: theme.gray[500],
    },
    prizeValueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    prizeValue: {
      fontSize: 24,
      fontWeight: '900',
      color: theme.primary,
      letterSpacing: -0.5,
    },
    prizeValueMuted: {
      color: theme.gray[500],
    },
    coinIconSmall: {
      width: 22,
      height: 22,
    },
    // ── Company top-prize ─────────────────────────────────────────────────────
    topPrizeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 6,
      maxWidth: 168,
    },
    topPrizeImage: {
      width: 30,
      height: 30,
      borderRadius: 7,
      backgroundColor: theme.white,
      borderWidth: 1,
      borderColor: theme.gray[200],
      flexShrink: 0,
    },
    topPrizeMedal: {
      fontSize: 22,
      flexShrink: 0,
    },
    topPrizeLabel: {
      flexShrink: 1,
      fontSize: 14,
      fontWeight: '800',
      color: theme.black,
      letterSpacing: -0.2,
      textAlign: 'right',
    },
  });
